// #region Imports
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { Currency, formatCurrency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import { useGetIndividualByIdQuery, useGetIndividualsByUserIdsQuery, individualsApi } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualAvatar,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  useCreateChatForumForNetworkConnectionMutation,
  useGetChatForumForNetworkConnectionQuery
} from "users/networking/connections/_api/user-network-connection-api";
import { NetworkConnectionForumJSON } from "users/networking/connections/_schemas/network-connection-forum-schema";
import { selectIsNetworkedWith, selectNetworkConnectionByUserId } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  paddingBottom: theme.spacing(8),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(3, 0),
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ChatContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    minWidth: "unset",
    padding: 0,
  },
  [theme.breakpoints.up("md")]: {
    minWidth: "500px",
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    minWidth: "500px",
    padding: theme.spacing(0, 4),
  },
  gridColumn: "1 / auto",
  gridRowStart: "2",
  gridRowEnd: "none",
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
    margin: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    margin: theme.spacing(0, 4),
  },
  columnFill: "balance",
  columnGap: theme.spacing(1),
  gridColumn: "1 / auto",
  gridRowStart: "2",
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    justifyItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "min-content auto",
    gridTemplateRows: "min-content auto",
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "min-content auto 1fr",
  },
  alignItems: "flex-start",
  gap: theme.spacing(2),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  "& > *:not(:last-child)": {
    marginRight: theme.spacing(2),
  },
}));
const GridContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(15),
  width: theme.spacing(15),
}));
const FieldTitle = styled(Typography)(() => ({
  fontWeight: 500,
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  [theme.breakpoints.up("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.up("lg")]: {
    gridColumn: "3",
    gridRow: "1",
    margin: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
  margin: 0,
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.1em",
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const ResumeLink = styled(Link)(() => ({
  cursor: "pointer",
}));
const MessageButtonContainer = styled("span")<{ networked: boolean }>(({ networked: inNetwork }) => ({
  cursor: inNetwork ? "pointer" : "not-allowed",
}));
// #endregion

export interface ViewIndividualProfileProps {
  profile?: IndividualProfile;
  individualId?: Guid;
  userId?: Guid;
}

export default function ViewIndividualProfile(props: Readonly<ViewIndividualProfileProps>) {
  const {
    profile,
    individualId,
    userId
  } = props;

  // #region State
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [activeChatForum, setActiveChatForum] = useState<Forum | undefined>();

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  // #region Queries
  const individualByIdQuery = useGetIndividualByIdQuery(
    individualId ? { individualId: individualId.value } : skipToken,
    { skip: !!profile || !individualId }
  );

  const individualByUserIdsQuery = useGetIndividualsByUserIdsQuery(
    userId ? { userIds: new Set([userId.value]) } : skipToken
  );

  const individual = useMemo(() => {
    let individualData = individualByIdQuery.data;
    if(!individualData && individualByUserIdsQuery.data?.length) {
      individualData = individualByUserIdsQuery.data[0];
    }
    if (!individualData) return null;
    return Individual.fromJSON(individualData);
  }, [individualByUserIdsQuery.data, individualByIdQuery.data]);

  const individualProfile = profile ?? individual?.profile;
  const viewingOwnProfile = individualId?.isEqualTo(session.user?.individualId);

  const networkForumQuery = useGetChatForumForNetworkConnectionQuery(
    individualProfile?.userId ? { userId: individualProfile.userId } : skipToken
  );
  const [createChat, createChatResult] = useCreateChatForumForNetworkConnectionMutation();

  const deserializeChatForum = (forumData?: NetworkConnectionForumJSON | null): Forum | null => {
    if (!forumData) return null;

    return Forum.fromJSON({
      id: forumData.id,
      name: forumData.name,
      createdDate: forumData.createdDate,
      entityClass: forumData.entityClass,
      entityId: forumData.entityId,
      context: forumData.context,
      subscribers: forumData.subscriberIds?.map((forumUserId: string) => ({
        userId: forumUserId,
        legalEntityId: Guid.EMPTY.value
      })) ?? [],
    });
  };

  const chat = useMemo(() => {
    return deserializeChatForum(networkForumQuery.data ?? createChatResult.data);
  }, [
    createChatResult.data,
    networkForumQuery.data,
  ]);
  const currentChatForum = activeChatForum ?? chat ?? undefined;

  // #region Selectors
  const avatar = useSelector(getIndividualAvatar(individualId ?? null));
  const networkConnection = useSelector(individualProfile?.userId
    ? selectNetworkConnectionByUserId(individualProfile.userId)
    : () => null
  );
  const isNetworkedWith = useSelector(individualProfile?.userId
    ? selectIsNetworkedWith(individualProfile.userId)
    : () => false
  );

  useEffect(() => {
    if (individualByIdQuery.isError) {
      enqueueSnackbar("Unable to load profile", { variant: "error" });
    }
  }, [individualByIdQuery.isError]);

  // #endregion

  async function downloadResume() {
    if (!individualProfile?.resumeId || !individualId) return;
    try {
      const { blob, filename } = await dispatch(
        individualsApi.endpoints.downloadIndividualResume.initiate({ individualId: individualId.value })
      ).unwrap();
      const link = document.createElement("a");
      link.setAttribute("href", globalThis.URL.createObjectURL(blob));
      if (filename) link.setAttribute("download", filename);
      link.setAttribute("target", "_blank");
      link.click();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to download resume", { variant: "error" });
    }
  }

  function hideIfNotIPAttorney() {
    return {
      display: individualProfile?.categories?.includes("IPAttorney") ? "grid" : "none",
    };
  }

  async function createForumForNetworkConnection(): Promise<Forum | null> {
    if (chat) return chat;
    if (!networkConnection?.connectionId) return null;

    try {
      const forumData = await createChat({ connectionId: networkConnection.connectionId }).unwrap();
      const nextForum = deserializeChatForum(forumData);
      if (!nextForum) {
        throw new Error("Unable to create chat forum");
      }

      setActiveChatForum(nextForum);
      return nextForum;
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to create chat forum", { variant: "error" });
      return null;
    }
  }

  // #region Render
  return (
    <MainContainer>
      <HeaderContainer>
        <HeaderInnerContainer>
          <StyledAvatar src={avatar} />
          <TitleContainer>
            <NameChipContainer>
              <Typography variant="h4">
                {individualProfile?.firstName} {individualProfile?.lastName}
              </Typography>
              <span>
                {individualProfile?.categories?.map((category) => (
                  <ProfileTypeChip key={category} label={category} size="small" />
                ))}
              </span>
            </NameChipContainer>
            {individualProfile?.userId && (
              <ButtonContainer>
                <NetworkButton
                  userId={individualProfile?.userId}
                />
                <Tooltip
                  title={(function () {
                    if (chatOpen) return "Back to profile";
                    if (isNetworkedWith) return chat ? "Send message" : "Start conversation";
                    if (viewingOwnProfile) return "You can't send a message to yourself";
                    return "You must be in the user's network to send a message";
                  })()}
                >
                  <MessageButtonContainer networked={isNetworkedWith}>
                    <Button
                      variant="contained"
                      startIcon={chatOpen ? <BadgeIcon /> : <MessageIcon />}
                      disableElevation
                      disabled={!isNetworkedWith || viewingOwnProfile}
                      color="primary"
                      onClick={async () => {
                        if (chatOpen) {
                          setChatOpen(false);
                          return;
                        }

                        const nextForum = currentChatForum ?? await createForumForNetworkConnection();
                        if (!nextForum) return;

                        setActiveChatForum(nextForum);
                        setChatOpen(true);
                      }}
                    >
                      {chatOpen ? "Back to profile" : "Message"}
                    </Button>
                  </MessageButtonContainer>
                </Tooltip>
              </ButtonContainer>
            )}
          </TitleContainer>
          <DescriptionContainer>
            <DescriptionText variant="subtitle1">{individualProfile?.credentials}</DescriptionText>
            <DescriptionDivider />
          </DescriptionContainer>
        </HeaderInnerContainer>
      </HeaderContainer>
      <ChatContainer>
        {individualByIdQuery.isLoading && <Loader />}
        {chatOpen && currentChatForum && (
          <ChatDialog
            loadedForum={currentChatForum}
          />
        )}
      </ChatContainer>
      <ContentContainer>
        {!chatOpen && !individualByIdQuery.isLoading && (
          <>
            {individualProfile?.professionalRegistration && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>Professional Registration #</FieldTitle>
                <Typography>{individualProfile?.professionalRegistration?.value}</Typography>
              </GridContainer>
            )}
            {individualProfile?.regionsRegistered && individualProfile.regionsRegistered.length > 0 && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>Regions Registered</FieldTitle>
                <Typography>{individualProfile?.regionsRegistered?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.technicalBackground && (
              <GridContainer>
                <FieldTitle>Technical Background</FieldTitle>
                <Typography>{individualProfile?.technicalBackground?.value}</Typography>
              </GridContainer>
            )}
            {individualProfile?.school && (
              <GridContainer>
                <FieldTitle>Undergraduate School / Law School</FieldTitle>
                <Typography>{individualProfile?.school}</Typography>
              </GridContainer>
            )}
            {individualProfile?.degree && (
              <GridContainer>
                <FieldTitle>Degree(s)</FieldTitle>
                <Typography>{individualProfile?.degree}</Typography>
              </GridContainer>
            )}
            {individualProfile?.numberOfApplicationsDraftedProsecuted && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>Number Applications Drafted / Prosecuted</FieldTitle>
                <Typography>{individualProfile?.numberOfApplicationsDraftedProsecuted}</Typography>
              </GridContainer>
            )}
            {individualProfile?.numberOfAllowedApplications && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>Number Allowed Applications</FieldTitle>
                <Typography>{individualProfile?.numberOfAllowedApplications}</Typography>
              </GridContainer>
            )}
            {individualProfile?.barAdmissionYear && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>Year Of Bar Admission</FieldTitle>
                <Typography>{individualProfile?.barAdmissionYear}</Typography>
              </GridContainer>
            )}
            {individualProfile?.fluentLanguages && individualProfile.fluentLanguages.length > 0 && (
              <GridContainer>
                <FieldTitle>Fluent Languages</FieldTitle>
                <Typography>{individualProfile?.fluentLanguages?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.technicalLanguages && individualProfile.technicalLanguages.length > 0 && (
              <GridContainer>
                <FieldTitle>Technical Languages</FieldTitle>
                <Typography>{individualProfile?.technicalLanguages?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.regionsLicensed && individualProfile.regionsLicensed.length > 0 && (
              <GridContainer sx={hideIfNotIPAttorney()}>
                <FieldTitle>States Licensed In</FieldTitle>
                <Typography>{individualProfile?.regionsLicensed?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.insured && (individualProfile?.insured?.insuredAmount ?? 0) > 0 && (
              <GridContainer>
                <FieldTitle>Insured Amount</FieldTitle>
                <Typography>
                  {formatCurrency(
                    new Money(
                      Number.parseFloat(individualProfile?.insured?.insuredAmount?.toString() ?? "0"),
                      Currency.USD
                    )
                  )}
                </Typography>
              </GridContainer>
            )}
            {individualProfile?.billingRate?.billingRate && individualProfile?.billingRate?.billingRate !== "" && (
              <GridContainer>
                <FieldTitle>Billing Rate</FieldTitle>
                <Typography>
                  {formatCurrency(
                    new Money(Number.parseFloat(individualProfile?.billingRate?.billingRate ?? "0"), Currency.USD)
                  )}
                </Typography>
                <FieldTitle>Billing Unit</FieldTitle>
                <Typography>{individualProfile?.billingRate?.billingUnit ?? "N/A"}</Typography>
              </GridContainer>
            )}
            {individualProfile?.phoneNumber?.value && individualProfile.phoneNumber.value !== "" && (
              <GridContainer>
                <FieldTitle>Phone Number</FieldTitle>
                <Link href={`tel:${individualProfile?.phoneNumber?.value}`}>
                  {individualProfile?.phoneNumber?.value}
                </Link>
              </GridContainer>
            )}
            {individualProfile?.email?.value && individualProfile?.email?.value !== "" && (
              <GridContainer>
                <FieldTitle>Email Address</FieldTitle>
                <Link href={`mailto:${individualProfile?.email?.value}`}>{individualProfile?.email?.value}</Link>
              </GridContainer>
            )}
            <GridContainer>
              <FieldTitle>On Call</FieldTitle>
              {individualProfile?.isOnCall ? "Yes" : "No"}
            </GridContainer>
            <GridContainer>
              <FieldTitle>Potential Diversity Candidate</FieldTitle>
              {individualProfile?.potentialDiversityCandidate ? "Yes" : "No"}
            </GridContainer>
            <GridContainer>
              <FieldTitle>Collaborates With Other Individuals</FieldTitle>
              {individualProfile?.collaborates ? "Yes" : "No"}
            </GridContainer>
            {individualProfile?.professionalPublications && individualProfile?.professionalPublications.length > 0 && (
              <GridContainer>
                <FieldTitle>Professional Publications</FieldTitle>
                {individualProfile?.professionalPublications?.map((publication) => (
                  <Link href={publication} key={publication} target="_blank" rel="noopener">
                    {publication}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individualProfile?.sampleApplications && individualProfile?.sampleApplications.length > 0 && (
              <GridContainer>
                <FieldTitle>Sample Applications</FieldTitle>
                {individualProfile?.sampleApplications?.map((application) => (
                  <Link href={application} key={application} target="_blank" rel="noopener">
                    {application}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individualProfile?.links && individualProfile?.links.length > 0 && (
              <GridContainer>
                <FieldTitle>Other Links</FieldTitle>
                {individualProfile?.links?.map((link) => (
                  <Link href={link} key={link} target="_blank" rel="noopener">
                    {link}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individual?.profile.resumeId && (
              <GridContainer>
                <FieldTitle>Resume</FieldTitle>
                <ResumeLink onClick={downloadResume}>View resume</ResumeLink>
              </GridContainer>
            )}
          </>
        )}
      </ContentContainer>
    </MainContainer>
  );
}
